import React from 'react';

import { gql, useQuery } from '@apollo/client';
import { Add, Edit } from '@mui/icons-material';
import { Alert, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import { includes, isEmpty, map, size } from 'lodash';
import { Link } from 'react-router-dom';
import { BottomSafeArea, ContainerFlexHeight, LoadingSpinner } from '../../components';
import Protector from '../../components/protector';
import { createScopedI18n } from '../../i18n/i18n';

const usersPageI18n = createScopedI18n('pages.users');

export const getChainUsersGql = gql`
  query getChainUsers {
    user {
      id
      chain {
        id
        users {
          id
          username
          email
          roles
          restaurants {
            id
          }
        }
      }
    }
  }
`;

type User = {
  id: string;
  username?: string | null;
  email?: string | null;
  roles?: string[] | null;
  restaurants?: { id: string }[] | null;
};

type ChainUsersData = {
  user: {
    id: string;
    chain?: {
      id: string;
      users?: User[];
    } | null;
  };
};

const UsersPage = () => {
  const { data: userData, loading: userLoading } = useQuery<ChainUsersData>(getChainUsersGql, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const currentUserId = userData?.user?.id;
  const users = React.useMemo(() => userData?.user?.chain?.users ?? [], [userData?.user?.chain?.users]);

  const canEditUser = React.useCallback(
    (user: User) => currentUserId !== user.id && !includes(user.roles, 'restaurant_root'),
    [currentUserId],
  );

  if (userLoading) {
    return <LoadingSpinner />;
  }

  if (isEmpty(users)) {
    return (
      <ContainerFlexHeight
        maxWidth="sm"
        sx={{ paddingY: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Alert severity="warning">{usersPageI18n('blank')}</Alert>
      </ContainerFlexHeight>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ paddingY: 2 }}>
      <Stack gap={1}>
        {map(users, (user) => (
          <Card key={user.id}>
            <CardContent>
              <Stack flexDirection="row" gap={1}>
                <Stack flex={1} gap={0.5}>
                  <Typography>
                    <Typography component="span" sx={{ minWidth: 110, display: 'inline-block', paddingRight: 0.5 }}>
                      {user.username}
                    </Typography>
                    {user.email}
                  </Typography>
                  <Typography>{usersPageI18n('manage_restaurant_count', { count: size(user.restaurants) })}</Typography>
                </Stack>
                <Stack>
                  <Button
                    component={Link}
                    to={user.id}
                    variant={'contained'}
                    disableElevation
                    startIcon={canEditUser(user) ? <Edit /> : undefined}
                  >
                    {canEditUser(user) ? usersPageI18n('edit') : usersPageI18n('show')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Protector permissions={['userCreate']}>
        <Protector.Allowed>
          <Button
            component={Link}
            to="new"
            variant="contained"
            disableElevation
            startIcon={<Add />}
            sx={{ marginTop: 2 }}
            fullWidth
          >
            {usersPageI18n('add_user')}
          </Button>
        </Protector.Allowed>
      </Protector>

      <BottomSafeArea />
    </Container>
  );
};

export default UsersPage;
